import { useTranslation } from '@kgc/i18n';
import { Separator } from '@kgc/ui';
import { CheckCircle, ShieldCheck, XCircle, Zap } from 'lucide-react';
import { useMemo } from 'react';
import { ApprovalCard } from './components/ApprovalCard';
import type { ApprovalRequest } from './types';

function formatTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString('hu-HU', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

interface FlowApprovalsTabProps {
  approvals: ApprovalRequest[];
  onResolved?: (() => void) | undefined;
}

export function FlowApprovalsTab({ approvals, onResolved }: FlowApprovalsTabProps) {
  const { t } = useTranslation();

  const pending = useMemo(
    () => approvals.filter(a => a.status === 'pending' || a.status === 'requested'),
    [approvals]
  );

  const past = useMemo(
    () =>
      approvals.filter(
        a => a.status === 'approved' || a.status === 'rejected' || a.status === 'escalated'
      ),
    [approvals]
  );

  if (pending.length === 0 && past.length === 0) {
    return (
      <div
        className="flex flex-col items-center justify-center py-12 text-muted-foreground"
        data-testid="flow-approvals-empty"
      >
        <ShieldCheck className="h-12 w-12 mb-3 opacity-50" aria-hidden="true" />
        <p className="text-sm">{t('integration.flow.approval.noPending')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Pending approvals */}
      {pending.length > 0 && (
        <div className="space-y-2">
          {pending.map(a => (
            <ApprovalCard key={a.id} approval={a} onResolved={onResolved} />
          ))}
        </div>
      )}

      {/* Past decisions */}
      {past.length > 0 && (
        <div>
          {pending.length > 0 && <Separator className="my-4" />}
          <h3 className="text-sm font-medium text-muted-foreground mb-3">
            {t('integration.flow.approval.pastDecisions')}
          </h3>
          <div className="space-y-1.5">
            {past.map(a => {
              const icon =
                a.status === 'approved' ? (
                  <CheckCircle className="h-4 w-4 text-green-500" aria-hidden="true" />
                ) : a.status === 'rejected' ? (
                  <XCircle className="h-4 w-4 text-red-500" aria-hidden="true" />
                ) : (
                  <Zap className="h-4 w-4 text-yellow-500" aria-hidden="true" />
                );

              return (
                <div
                  key={a.id}
                  className="flex items-center gap-2 text-xs text-muted-foreground"
                  data-testid={`flow-past-decision-${a.id}`}
                >
                  {icon}
                  <span className="text-foreground">
                    {a.entityType} #{a.entityId}
                  </span>
                  <span>—</span>
                  <span>{a.approvedBy ?? a.rejectedBy ?? '—'}</span>
                  <span>{a.resolvedAt ? formatTime(a.resolvedAt) : ''}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
